import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  MessageCircleQuestion,
  Pause,
  Play,
  Search,
} from 'lucide-react';
import { usePublicFaqPage } from '@/apis/actualFaqs/actual';
import { usePreferenceStore } from '@/stores/usePreferenceStore';

const FaqAnswerDrawer = lazy(() => import('./HomeFaqAnswerDrawer'));
const ROTATION_MS = 2500;

export default function HomeFaqQuestions({
  className = '',
}: {
  className?: string;
}) {
  const display = usePreferenceStore(
    (s) => `${s.locale ?? 'en'}:${s.currency}`,
  );
  return <Questions key={display} display={display} className={className} />;
}

function Questions({
  display,
  className,
}: {
  display: string;
  className: string;
}) {
  const { t } = useTranslation('home');
  const root = useRef<HTMLElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(!document.hidden);
  const [inView, setInView] = useState(true);
  const [reduced, setReduced] = useState(
    () =>
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  const [openedId, setOpenedId] = useState<string>();
  const query = usePublicFaqPage(
    { pageNum: 1, pageSize: 20 },
    display,
    visible,
  );
  const rows = query.isError
    ? []
    : [...(query.data?.rows ?? [])]
        .sort(
          (a, b) =>
            Date.parse(b.publishedAt) - Date.parse(a.publishedAt) ||
            a.id.localeCompare(b.id),
        )
        .slice(0, 10);
  const current = rows[index % (rows.length || 1)];
  const sequence = rows.map((r) => `${r.id}:${r.version}`).join(',');

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media?.matches ?? false);
    const visibility = () => setVisible(!document.hidden);
    media?.addEventListener('change', update);
    document.addEventListener('visibilitychange', visibility);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? undefined
        : new IntersectionObserver((entries) =>
            setInView(entries[0]?.isIntersecting ?? false),
          );
    if (root.current) observer?.observe(root.current);
    return () => {
      media?.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', visibility);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      rows.length < 2 ||
      paused ||
      hovered ||
      focused ||
      !visible ||
      !inView ||
      reduced ||
      openedId
    )
      return;
    const timer = window.setInterval(
      () => setIndex((value) => (value + 1) % rows.length),
      ROTATION_MS,
    );
    return () => window.clearInterval(timer);
  }, [
    sequence,
    rows.length,
    paused,
    hovered,
    focused,
    visible,
    inView,
    reduced,
    openedId,
  ]);

  const step = (delta: number) => {
    setPaused(true);
    setIndex((value) => (value + delta + rows.length) % rows.length);
  };

  return (
    <section
      ref={root}
      data-gp-reveal
      data-testid="home-faq-questions"
      aria-label={t('searchPlaceholder')}
      className={`gp-home-faq relative z-10 rounded-[28px] border border-gp-pink-100 bg-white p-4 shadow-glow ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocused(false);
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="grid size-9 shrink-0 place-items-center rounded-full bg-gp-pink-100/70 text-gp-pink-700"
          aria-hidden="true"
        >
          <MessageCircleQuestion className="size-5" />
        </span>
        <h2 className="min-w-0 flex-1 text-[12px] font-medium text-gp-ink-600">
          {t('searchPlaceholder')}
        </h2>
        <Link
          to="/search"
          aria-label={t('faq.searchAll')}
          className="grid size-9 shrink-0 place-items-center rounded-full text-gp-pink-700 transition-colors hover:bg-gp-pink-50 focus-visible:outline-2 focus-visible:outline-gp-pink-700"
        >
          <Search className="size-4" aria-hidden="true" />
        </Link>
      </div>
      <div className="mt-2 h-14 overflow-hidden" aria-live="off">
        {current ? (
          <button
            ref={trigger}
            type="button"
            onClick={() => setOpenedId(current.id)}
            className="flex h-full w-full items-center gap-3 text-left text-[16px] leading-6 font-semibold text-gp-pink-800 focus-visible:rounded-xl focus-visible:outline-2 focus-visible:outline-gp-pink-700"
          >
            <span
              key={current.id}
              className="gp-faq-question min-w-0 flex-1 line-clamp-2 wrap-anywhere"
            >
              {current.question}
            </span>
            <ArrowRight className="size-5 shrink-0" aria-hidden="true" />
          </button>
        ) : (
          <Link
            to="/help#faqs"
            className="flex h-full items-center gap-3 text-[15px] leading-6 font-semibold text-gp-pink-800"
          >
            <span className="min-w-0 flex-1">{t('faq.fallback')}</span>
            <ArrowRight className="size-5 shrink-0" aria-hidden="true" />
          </Link>
        )}
      </div>
      <div className="mt-2 flex min-h-8 items-center justify-between gap-2 border-t border-gp-pink-100/70 pt-2">
        <Link
          to="/help#faqs"
          className="inline-flex min-h-8 items-center text-[11px] font-semibold text-gp-ink-600 underline-offset-4 hover:underline"
        >
          {t('faq.browseAll')}
        </Link>
        {rows.length > 1 && (
          <div className="flex shrink-0 items-center gap-1">
            <span
              className="mr-1 text-[10px] tabular-nums text-gp-ink-600"
              aria-label={t('faq.position', {
                current: (index % rows.length) + 1,
                total: rows.length,
              })}
            >
              {String((index % rows.length) + 1).padStart(2, '0')} /{' '}
              {String(rows.length).padStart(2, '0')}
            </span>
            <button
              type="button"
              aria-label={t('faq.previous')}
              onClick={() => step(-1)}
              className="grid size-8 place-items-center rounded-full text-gp-pink-700 hover:bg-gp-pink-50"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </button>
            {!reduced && (
              <button
                type="button"
                aria-label={t(paused ? 'faq.play' : 'faq.pause')}
                onClick={() => setPaused((value) => !value)}
                className="grid size-8 place-items-center rounded-full text-gp-pink-700 hover:bg-gp-pink-50"
              >
                {paused ? (
                  <Play className="size-3.5" aria-hidden="true" />
                ) : (
                  <Pause className="size-3.5" aria-hidden="true" />
                )}
              </button>
            )}
            <button
              type="button"
              aria-label={t('faq.next')}
              onClick={() => step(1)}
              className="grid size-8 place-items-center rounded-full text-gp-pink-700 hover:bg-gp-pink-50"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {openedId && (
        <Suspense
          fallback={
            <p role="status" className="sr-only">
              {t('faq.loading')}
            </p>
          }
        >
          <FaqAnswerDrawer
            id={openedId}
            display={display}
            trigger={trigger}
            onClose={() => setOpenedId(undefined)}
          />
        </Suspense>
      )}
    </section>
  );
}
